export const DefaultEnableArmControl = false;

/** Joystick mapping while arm control is active. */
export const ARM_STICK_MODE_TRANSLATE = "translate";
export const ARM_STICK_MODE_ROTATE = "rotate";
export const DefaultArmStickMode = ARM_STICK_MODE_TRANSLATE;

/** Linear scale (m/s) at stick full deflection * speedMultiplier */
export const ARM_LINEAR_SPEED = 0.15;
/** Angular scale (rad/s) at stick full deflection * speedMultiplier */
export const ARM_ANGULAR_SPEED = 0.5;

/** Trapezoidal moveJ profile (matches server DEFAULT_MOVEJ_PROFILE). */
export const DEFAULT_MOVEJ_PROFILE = {
    speed: 1.0,
    aggressiveness: 0.5,
    damping: 0.5,
};

export const MOVEJ_PROFILE_LIMITS = {
    speed: { min: 0.25, max: 2.0, step: 0.05 },
    aggressiveness: { min: 0, max: 1, step: 0.05 },
    damping: { min: 0, max: 1, step: 0.05 },
};

/**
 * @param {unknown} raw
 * @returns {{ speed: number, aggressiveness: number, damping: number }}
 */
export const normalizeMovejProfile = (raw) => {
    const src = raw && typeof raw === "object" ? raw : {};
    const clamp = (v, min, max, fallback) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return fallback;
        return Math.min(max, Math.max(min, n));
    };
    return {
        speed: clamp(src.speed, MOVEJ_PROFILE_LIMITS.speed.min, MOVEJ_PROFILE_LIMITS.speed.max, DEFAULT_MOVEJ_PROFILE.speed),
        aggressiveness: clamp(
            src.aggressiveness,
            MOVEJ_PROFILE_LIMITS.aggressiveness.min,
            MOVEJ_PROFILE_LIMITS.aggressiveness.max,
            DEFAULT_MOVEJ_PROFILE.aggressiveness
        ),
        damping: clamp(
            src.damping,
            MOVEJ_PROFILE_LIMITS.damping.min,
            MOVEJ_PROFILE_LIMITS.damping.max,
            DEFAULT_MOVEJ_PROFILE.damping
        ),
    };
};

/** Elevator press offsets in meters (matches server DEFAULT_PRESS_OFFSETS). */
export const DEFAULT_PRESS_OFFSETS = {
    approach_offset_m: 0.02,
    press_depth_m: 0.01,
    /** Cam-frame X tip aim bias (ibvs_target_offset_cam_m[0], + = right). */
    tip_offset_x_m: 0.0,
    /** Cam-frame Y tip aim bias (ibvs_target_offset_cam_m[1], + = down). */
    tip_offset_y_m: 0.03,
};

export const PRESS_OFFSET_LIMITS = {
    approach_offset_m: { min: 0.005, max: 0.2, step: 0.005 },
    press_depth_m: { min: 0, max: 0.08, step: 0.005 },
    tip_offset_x_m: { min: -0.08, max: 0.08, step: 0.005 },
    tip_offset_y_m: { min: -0.02, max: 0.08, step: 0.005 },
};

/**
 * @param {unknown} raw
 * @returns {{ approach_offset_m: number, press_depth_m: number, tip_offset_x_m: number, tip_offset_y_m: number }}
 */
export const normalizePressOffsets = (raw) => {
    const src = raw && typeof raw === "object" ? raw : {};
    const clamp = (v, min, max, fallback) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return fallback;
        return Math.min(max, Math.max(min, n));
    };
    return {
        approach_offset_m: clamp(
            src.approach_offset_m,
            PRESS_OFFSET_LIMITS.approach_offset_m.min,
            PRESS_OFFSET_LIMITS.approach_offset_m.max,
            DEFAULT_PRESS_OFFSETS.approach_offset_m
        ),
        press_depth_m: clamp(
            src.press_depth_m,
            PRESS_OFFSET_LIMITS.press_depth_m.min,
            PRESS_OFFSET_LIMITS.press_depth_m.max,
            DEFAULT_PRESS_OFFSETS.press_depth_m
        ),
        tip_offset_x_m: clamp(
            src.tip_offset_x_m,
            PRESS_OFFSET_LIMITS.tip_offset_x_m.min,
            PRESS_OFFSET_LIMITS.tip_offset_x_m.max,
            DEFAULT_PRESS_OFFSETS.tip_offset_x_m
        ),
        tip_offset_y_m: clamp(
            src.tip_offset_y_m,
            PRESS_OFFSET_LIMITS.tip_offset_y_m.min,
            PRESS_OFFSET_LIMITS.tip_offset_y_m.max,
            DEFAULT_PRESS_OFFSETS.tip_offset_y_m
        ),
    };
};

export const armStatesInfo = {
    state: {
        topic: "/arm/state",
        type: "std_msgs/String",
        default: null,
        transform: (msg) => {
            const raw = typeof msg === "string" ? msg : msg?.data;
            if (!raw) return null;
            try {
                return typeof raw === "string" ? JSON.parse(raw) : raw;
            } catch {
                return null;
            }
        },
    },
};

export const ARM_HEALTH_TOPIC = "/arm/health";

/** Latched Molmo (u,v) for Arm Cam overlay during elevator press. */
export const ARM_MOLMO_POINT_TOPIC = "/arm/molmo_point";

/**
 * Parse /arm/molmo_point std_msgs/String JSON.
 * @returns {{ u: number, v: number, imageWidth: number, imageHeight: number, button: string } | null}
 */
export const parseArmMolmoPointMsg = (msg) => {
    const raw = typeof msg === "string" ? msg : msg?.data;
    if (!raw) return null;
    let data = raw;
    if (typeof raw === "string") {
        try {
            data = JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (!data || typeof data !== "object") return null;
    if (data.clear === true) return null;
    const u = Number(data.u);
    const v = Number(data.v);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;
    return {
        u,
        v,
        imageWidth: Number(data.image_width) || 0,
        imageHeight: Number(data.image_height) || 0,
        button: typeof data.button === "string" ? data.button : "",
    };
};
